import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { GoogleMapsProvider, loadGoogleMaps } from '~/components/AddressAutocomplete';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreStatic } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { prevBusinessIdKey, useSwitchBusiness } from '~/hooks/useSwitchBusiness';
import { useHomePage } from '~/pages/HomePage/HomePageProvider';
import { clearPersistedOnboardingProgress, deriveVobMode, readPersistedCurrentStep } from './hooks/useOnboardingFlow';
import { VendorOnboarding } from './VendorOnboarding';
export default function VendorOnboardingPage() {
    var _ref;
    const history = useHistory();
    const { SP_ONBOARDING_PHASE_1 = false } = useFeatureFlags();
    const { localStorage } = useIoContext();
    const { switchBusiness } = useSwitchBusiness();
    const { setHidden } = useHomePage();
    const { getState } = useStoreStatic();
    const userInfo = useStore((state)=>state.userInfo);
    const isClient = userInfo === null || userInfo === void 0 ? void 0 : userInfo.isCustomer();
    const workspaces = (_ref = userInfo === null || userInfo === void 0 ? void 0 : userInfo.workspaces()) !== null && _ref !== void 0 ? _ref : [];
    const handleComplete = useCallback(()=>{
        clearPersistedOnboardingProgress();
        history.replace(routes.vendorDashboard);
    }, [
        history
    ]);
    const switchWithHidden = useCallback(async (businessId)=>{
        setHidden(true);
        try {
            await switchBusiness(businessId);
        } finally{
            setHidden(false);
        }
    }, [
        setHidden,
        switchBusiness
    ]);
    const handleClose = useCallback(async ()=>{
        var _ref;
        var _getState_claims, _userWorkspaces_find;
        clearPersistedOnboardingProgress();
        const currentUserInfo = getState().userInfo;
        const currentBusinessId = currentUserInfo === null || currentUserInfo === void 0 ? void 0 : currentUserInfo.businessId();
        const cookieBusinessId = (_getState_claims = getState().claims) === null || _getState_claims === void 0 ? void 0 : _getState_claims.businessId;
        const userWorkspaces = (_ref = currentUserInfo === null || currentUserInfo === void 0 ? void 0 : currentUserInfo.workspaces()) !== null && _ref !== void 0 ? _ref : [];
        const cookieIsOnCompletedWorkspace = cookieBusinessId != null && userWorkspaces.some((ws)=>ws.id() === cookieBusinessId && ws.isCompletelySetup());
        if (cookieIsOnCompletedWorkspace) {
            const worksAtMissing = (currentUserInfo === null || currentUserInfo === void 0 ? void 0 : currentUserInfo.business()) == null;
            if (worksAtMissing) {
                await switchWithHidden(cookieBusinessId);
                return;
            }
            history.replace(routes.home);
            return;
        }
        const stashedPrevBusinessId = localStorage.getItem(prevBusinessIdKey);
        const stashedIsAccessible = stashedPrevBusinessId != null && stashedPrevBusinessId !== currentBusinessId && userWorkspaces.some((ws)=>ws.id() === stashedPrevBusinessId);
        const targetBusinessId = stashedIsAccessible ? stashedPrevBusinessId : (_userWorkspaces_find = userWorkspaces.find((ws)=>ws.isCompletelySetup() && ws.id() !== currentBusinessId)) === null || _userWorkspaces_find === void 0 ? void 0 : _userWorkspaces_find.id();
        if (!targetBusinessId) return;
        await switchWithHidden(targetBusinessId);
    }, [
        getState,
        history,
        localStorage,
        switchWithHidden
    ]);
    const handleCloseRef = useRef(handleClose);
    useEffect(()=>{
        handleCloseRef.current = handleClose;
    }, [
        handleClose
    ]);
    useEffect(()=>{
        if (!SP_ONBOARDING_PHASE_1) return undefined;
        window.history.pushState(null, '', window.location.href);
        const handleBrowserBack = ()=>{
            void handleCloseRef.current();
        };
        window.addEventListener('popstate', handleBrowserBack);
        return ()=>window.removeEventListener('popstate', handleBrowserBack);
    }, [
        SP_ONBOARDING_PHASE_1
    ]);
    if (!SP_ONBOARDING_PHASE_1) return /*#__PURE__*/ _jsx(Redirect, {
        to: routes.home
    });
    const arrivedViaButton = localStorage.getItem(prevBusinessIdKey) != null;
    if (isClient && !arrivedViaButton) {
        return /*#__PURE__*/ _jsx(Redirect, {
            to: routes.home
        });
    }
    const currentBusinessId = userInfo === null || userInfo === void 0 ? void 0 : userInfo.businessId();
    const currentWorkspaceIsCompletedVendor = currentBusinessId != null && workspaces.some((ws)=>ws.isVendor() && ws.isCompletelySetup() && ws.id() === currentBusinessId);
    const hasPersistedProgress = readPersistedCurrentStep() != null;
    const claims = getState().claims;
    const claimsRequireVendorDetailsCompletion = (claims === null || claims === void 0 ? void 0 : claims.requireVendorDetailsCompletion) === true;
    const claimsRequireIdentity = (claims === null || claims === void 0 ? void 0 : claims.setIdentityOnly) === true;
    if (currentWorkspaceIsCompletedVendor && !hasPersistedProgress && !claimsRequireVendorDetailsCompletion && !claimsRequireIdentity) {
        return /*#__PURE__*/ _jsx(Redirect, {
            to: routes.home
        });
    }
    const vobMode = deriveVobMode({
        sp_onboarding_phase_1: !!SP_ONBOARDING_PHASE_1,
        isClient: !!isClient,
        hasCustomerWorkspace: workspaces.some((w)=>w.isCustomer()),
        hasCompletedVendorWorkspace: workspaces.some((w)=>w.isVendor() && w.isCompletelySetup()),
        hasIdentity: !!(userInfo === null || userInfo === void 0 ? void 0 : userInfo.hasIdentity())
    });
    return /*#__PURE__*/ _jsx(GoogleMapsProvider, {
        loadGoogleMaps: loadGoogleMaps,
        children: /*#__PURE__*/ _jsx(VendorOnboarding, {
            mode: vobMode,
            onComplete: handleComplete,
            onClose: handleClose
        })
    });
}
